import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { uuid4 } from '@sentry/utils';
import { CloseIcon } from '@sketchnote/icons';
import {
  CalenderIcon,
  CustomDateIcon,
  CustomEmailIcon,
  CustomMultiselectIcon,
  CustomNumberIcon,
  CustomPersonIcon,
  CustomPhoneIcon,
  CustomSelectIcon,
  CustomTextIcon,
  CustomUrlIcon,
  TickMarkIcon,
} from '@sketchnote/icons';
import { Button, Checkbox, LoaderSm } from '@sketchnote/shared-components';
import cn from 'classnames';
import { useRouter } from 'next/router';
import React, { Fragment, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import Select from 'react-select';
import { useFileStore } from 'src/features/files';
import { HeaderData } from 'src/features/files/types/file';
import { read, utils } from 'xlsx';

export const CsvFileMap = ({
  file,
  fileTitle,
  workspace,
  onClose,
}: {
  file: File;
  fileTitle: string;
  workspace: string | undefined;
  onClose: () => void;
}) => {
  const [fileHeader, setFileHeader] = useState<HeaderData[]>([]);
  const [fileData, setFileData] = useState<any>();
  const [tasknameIndex, setTasknameIndex] = useState(0);
  const onImportCsvFile = useFileStore((s) => s.onImportCsvFile);
  const [loaderIsOpen, setLoaderIsOpen] = useState(false);
  const [viewFile, setViewFile] = useState(false);
  const [newFile, setNewFile] = useState('');

  const generatedFileOptions = [
    { value: 'list', label: 'List' },
    { value: 'kanban', label: 'Kanban' },
  ];

  const [generatedFileType, setGeneratedFileType] = useState(generatedFileOptions[0]);

  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;

  useEffect(() => {
    const fetchedFileData = (result: [[string]]) => {
      const header = result[0];
      const headerData: HeaderData[] =
        header &&
        header.map((item) => ({
          id: uuid4(),
          label: item,
          type: 'text',
          include: true,
        }));
      headerData && setFileHeader(headerData);
      setFileData(result);
    };
    readUserFile(fetchedFileData);
  }, [file]);

  const readFile = async () => {
    const fileBuffer = await file[0]?.arrayBuffer();
    if (!fileBuffer) return;
    const wb = read(fileBuffer);
    const rows = utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1 });
    return rows;
  };

  const readUserFile = async (fetchedFileData: ([[string]]) => void) => {
    const data: any = await readFile();
    fetchedFileData(data);
  };

  const handleContinueImport = async () => {
    setLoaderIsOpen(true);
    const response: any = await onImportCsvFile({
      workspace,
      fileTitle,
      tasknameIndex,
      fileData,
      fileHeader,
      fileType: generatedFileType.value,
    });
    if (response) {
      setNewFile(response.file._id);
      setLoaderIsOpen(false);
      setViewFile(true);
    }
  };

  const handleToggleChecked = (e: any, itemId: string, index: number) => {
    setFileHeader((prevFileHeader) =>
      prevFileHeader.map((headerItem) => {
        if (headerItem.id !== itemId) return headerItem;
        return { ...headerItem, include: !headerItem.include };
      })
    );
  };

  const handleChangeFieldType = (item: HeaderData, e: any) => {
    setFileHeader((prevFileHeader) =>
      prevFileHeader.map((headerItem) => {
        if (headerItem.id !== item.id) return headerItem;
        return { ...headerItem, type: e.value };
      })
    );
  };

  const handleTaskNameChange = (index: number) => {
    setTasknameIndex(index);
  };

  const handleLoaderModal = () => {
    setLoaderIsOpen(false);
  };
  const handleViewFileModal = () => {
    setViewFile(false);
  };

  const showImportedFile = () => {
    onClose();
    router.push(`/${mode}/${orgId}/files/${newFile}`);
    setViewFile(false);
  };

  const handleFileTypeChange = (value: any) => {
    setGeneratedFileType(value);
  };

  return (
    <ErrorBoundary
      FallbackComponent={() => (
        <p contentEditable={false} className="p-10 mb-20 mt-10 bg-yellow-100">
          <span role="img" aria-label="Warning emoji">
            ⚠️
          </span>{' '}
          An unexpected error occurred while importing the file. Please try again.
        </p>
      )}
    >
      <div className="rounded-sm px-20 py-14">
        <header className="flex  pr-4 py-10 border-b h-90 justify-between items-center">
          <div className="flex flex-row items-center">
            <section className="flex flex-col font-medium">
              <span>🎯 {file[0]?.name}</span>
              <p className="text-xss text-gray-600">Map fields and Continue with the import</p>
            </section>
          </div>
          <div className="flex flex-row items-center">
            <section className="flex mr-12 items-center">
              <span className=" text-sm mr-8">File Type : </span>
              <Select
                value={generatedFileType}
                onChange={handleFileTypeChange}
                options={generatedFileOptions}
                className="text-sm text-gray-999 font-regular h-36 w-110"
              />
            </section>
            <section className="flex">
              <Button status="primary" className="ml-10" onClick={handleContinueImport}>
                Continue Import
              </Button>
            </section>
            <CloseIcon className="ml-10 flex text-xs" onClick={onClose} />
          </div>
        </header>
        {fileHeader && (
          <>
            <div className="flex items-center justify-between pt-16 pb-14">
              <section className="font-medium">{fileHeader.length} columns</section>
              <section className=" flex flex-row font-regular mr-6 items-center w-">
                <label className="flex pr-12">Select field for task name</label>
                <div className="flex">
                  <TaskNameSelect fileHeader={fileHeader} onChange={handleTaskNameChange} />
                </div>
              </section>
            </div>
            <main className="flex max-h-500 min-h-400" style={{ background: '#FFFF' }}>
              <div className="flex flex-col py-14 inline-block ">
                <table className="w-976 border text-center truncate items-center">
                  <thead className="">
                    <tr>
                      <th scope="col" className="text-sm font-medium text-gray-900 px-6 py-20 w-40">
                        #
                      </th>
                      <th
                        scope="col"
                        className="text-sm font-medium text-gray-900 px-6 py-20 w-318"
                      >
                        CSV file coulumn name
                      </th>
                      <th
                        scope="col"
                        className="text-sm font-medium text-gray-900 px-6 py-20 w-318"
                      >
                        Task field components
                      </th>
                      <th
                        scope="col"
                        className="text-sm font-medium text-gray-900 pl-6 py-20 w-318"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                </table>
                <div className="overflow-hidden overflow-scroll">
                  <table className="w-976 border text-center truncate items-center">
                    <tbody>
                      {fileHeader &&
                        fileHeader.map((item, index) => {
                          return (
                            <Fragment key={index}>
                              <tr className="border-b items-center">
                                <td className="w-40 px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r">
                                  {index + 1}
                                </td>
                                <td className="w-318 text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap border-r">
                                  {item.label}
                                </td>
                                <td className="w-318 text-sm text-gray-900 font-light px-6 py-4 mr-2 whitespace-nowrap border-r">
                                  <CustomSelect
                                    item={item}
                                    onChangeFieldType={handleChangeFieldType}
                                  />
                                </td>
                                <td className="w-318 text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap border-r">
                                  <span className="">
                                    <Checkbox
                                      wrapperClass="mb-15"
                                      key={index}
                                      checked={item.include}
                                      onChange={(e) => {
                                        handleToggleChecked(e, item.id, index);
                                      }}
                                    >
                                      Include column
                                    </Checkbox>
                                  </span>
                                </td>
                              </tr>
                            </Fragment>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            </main>
          </>
        )}
        <Modal isOpen={loaderIsOpen} onClose={handleLoaderModal}>
          <ModalOverlay />
          <ModalContent style={{ width: '38.4rem', padding: '30px', marginTop: '20rem' }}>
            <p className="font-semibold mb-20"> We&apos;re importing your file</p>
            <LoaderSm className="mx-auto text-xss" />
          </ModalContent>
        </Modal>

        <Modal isOpen={viewFile} onClose={handleViewFileModal}>
          <ModalOverlay />
          <ModalCloseButton />
          <ModalContent
            style={{
              maxWidth: '32rem',
              marginTop: '15rem',
              padding: '12px',
              borderRadius: '6px',
            }}
          >
            <div className="flex flex-row items-center">
              <div className="flex bg-green-600 rounded-full block w-20 h-20">
                <TickMarkIcon wrapperClass="text-white ml-4 my-5" size={12} />
              </div>
              &nbsp;&nbsp;
              <div className="flex font-medium">
                Your import is complete.
                <span className="text-blue-500 cursor-pointer" onClick={showImportedFile}>
                  &nbsp; View file
                </span>
              </div>
            </div>
          </ModalContent>
        </Modal>
      </div>
    </ErrorBoundary>
  );
};

const TaskNameSelect = ({
  fileHeader,
  onChange,
}: {
  fileHeader: HeaderData[];
  onChange: (index: number) => void;
}) => {
  const options = fileHeader.map((item, index) => ({
    value: item.label,
    index: index,
  }));

  const formatOptionLabel = ({ value, index }) => (
    <div className="flex">
      <span className="flex truncate">{value}</span>
    </div>
  );

  return (
    <Select
      defaultValue={options[0]}
      className={cn('text-sm text-gray-999 font-regular placeholder-gray-500 h-36 w-200')}
      formatOptionLabel={formatOptionLabel}
      options={options}
      menuPortalTarget={document.body}
      menuPlacement="auto"
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      onChange={(e) => {
        onChange(e?.index);
      }}
    />
  );
};

const CustomSelect = ({
  item,
  onChangeFieldType,
}: {
  item: HeaderData;
  onChangeFieldType: (item: HeaderData, e: any) => void;
}) => {
  const options = [
    { value: 'text', label: 'Text', Icon: CustomTextIcon },
    { value: 'number', label: 'Number', Icon: CustomNumberIcon },
    { value: 'date', label: 'Date', Icon: CustomDateIcon },
    { value: 'select', label: 'Select', Icon: CustomSelectIcon },
    { value: 'multiselect', label: 'MultiSelect', Icon: CustomMultiselectIcon },
    { value: 'person', label: 'Person', Icon: CustomPersonIcon },
    { value: 'phone', label: 'Phone', Icon: CustomPhoneIcon },
    { value: 'email', label: 'Email', Icon: CustomEmailIcon },
    { value: 'url', label: 'Url', Icon: CustomUrlIcon },
  ];

  const formatOptionLabel = ({ value, label, Icon }) => (
    <div className="flex flex-row items-center">
      <span className="flex">
        <Icon wrapperClass="text-gray-400 mr-10" size={24} />{' '}
      </span>
      <span className="flex">{label}</span>
    </div>
  );

  return (
    <Select
      defaultValue={options[0]}
      formatOptionLabel={formatOptionLabel}
      options={options}
      menuPortalTarget={document.body}
      menuPlacement="auto"
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      onChange={(e) => {
        onChangeFieldType(item, e);
      }}
    />
  );
};
